import React, { useCallback } from 'react';

import type { Nullable } from 'chaika-frontend-types/frontend-cms';

import { AsyncMultiSelect, OptionType } from '@components/Select';
import {
    EMPTY_IDS,
    LegalEntitySelectScope,
    useLegalEntitySelect,
} from '@organization/hooks/legalEntity/useLegalEntitySelect';

interface Props extends LegalEntitySelectScope {
    value?: string[];
    title?: string;
    placeholder?: string;
    dataTest?: string;
    disabled?: boolean;
    forcePlaceholder?: boolean;
    onChange(legalEntityIds: string[]): void;
}

export function LegalEntityAsyncMultiSelect({
    types,
    statuses,
    searchTypes,
    searchStatuses,
    value,
    title,
    placeholder,
    dataTest,
    disabled,
    forcePlaceholder,
    onChange,
}: Props): React.ReactElement {
    const selectedIds = value ?? EMPTY_IDS;
    const { defaultOptions, loadOptions, isLoading, primeIds } = useLegalEntitySelect({
        types,
        statuses,
        searchTypes,
        searchStatuses,
        selectedIds,
    });

    const handleChange = useCallback(
        (options: Nullable<OptionType<string>[]>): void => {
            const ids = options?.map(({ value: id }) => id) ?? [];
            onChange(ids);
            primeIds(ids);
        },
        [onChange, primeIds],
    );

    return (
        <AsyncMultiSelect
            dataTest={dataTest ?? 'LegalEntityAsyncMultiSelect'}
            disabled={disabled}
            title={title}
            placeholder={placeholder}
            forcePlaceholder={forcePlaceholder}
            selectedValues={selectedIds}
            defaultOptions={defaultOptions}
            loadOptions={loadOptions}
            isLoading={isLoading}
            onChange={handleChange}
        />
    );
}
